const HTML_ENTITY_RE = /&#?[a-zA-Z0-9]+;/g;
const DYNAMIC_SPACING = '\t';

export class RendererUtil {
  /**
   * Replaces the DYNAMIC_SPACING character in textContent with the appropriate number of spaces
   *
   * @param fixedWidth actual width of the line
   * @param textContent content of the text that will be converted fixed width
   * @param options
   *    If `options.force` is set to true, forces lines with no dynamic spacing character
   *      that exceeds the maximum width to be broken at the last blank space character
   * @return an array of lines (more than one in case the line is too big)
   */
  static makeLineFixedWidth(fixedWidth: number, textContent: string, { force } = { force: false }) {
    // required for line-break to be honored in the rendered receipt
    if (textContent.length === 0) {
      return [new Array(fixedWidth).fill(' ').join('')];
    }

    // In case the rendered text includes html entities, these should count for 1 character
    const textLen = getLen(textContent);
    const dynSpaceCount = textContent.split(DYNAMIC_SPACING).length - 1;
    let replacementLines;

    // if the line is too large, break it on the dynamic spacing character
    if (dynSpaceCount > 0 && textLen - DYNAMIC_SPACING.length >= fixedWidth) {
      replacementLines = textContent.split(DYNAMIC_SPACING).map((line, index) => {
        if (index > 0) {
          const lineLen = getLen(line);
          if (lineLen < fixedWidth) {
            return new Array(fixedWidth - lineLen).fill(' ').join('') + line;
          }
        }
        return line;
      });
    }
    // otherwise, if there is any dynamic spacing character,
    // replace it with the appropriate number of spaces
    else if (dynSpaceCount > 0) {
      replacementLines = dynamicSpacing(fixedWidth, textLen, textContent);
    }
    // otherwise, if force option is enabled and the text content exceeds the maximum width
    // with no dynamic spacing character, break on the last space before exceeding the specified max width
    else if (force && textLen > fixedWidth) {
      replacementLines = wordsWarping(textContent, fixedWidth);
    } else {
      replacementLines = [textContent];
    }

    // pad right in case a line with no dynamic spacing is smaller than the fixed width
    replacementLines = replacementLines.map((line) => {
      const lineLen = getLen(line);
      if (lineLen < fixedWidth) {
        const missingSpaces = new Array(fixedWidth - lineLen).fill(' ').join('');
        line += missingSpaces;
      }
      return line;
    });

    return replacementLines;
  }

  static getLineFixedWidthMaker(fixedWidth: number): (textContent: string) => string[] {
    return (textContent: string): string[] =>
      RendererUtil.makeLineFixedWidth(fixedWidth, textContent);
  }

  static timeFormat(duration: number, debugMode?: boolean) {
    const floor = (n: number) => Math.floor(n).toFixed(0);
    const pad0 = (n: number) => (n >= 10 ? floor(n) : `0${floor(n)}`);

    if (debugMode) {
      return `${duration / 1000}`;
    }
    if (duration < 1000) {
      return `${duration}ms`;
    }
    duration = Math.round(duration / 1000);

    if (duration < 60) {
      return `0:${pad0(duration)}`;
    }
    if (duration < 60 * 60) {
      return `${floor(duration / 60)}:${pad0(duration % 60)}`;
    }
    if (duration < 60 * 60 * 24) {
      return `${floor(duration / 60 / 60)}:${pad0((duration / 60) % 60)}:${pad0(duration % 60)}`;
    }
    return `${floor(duration / 60 / 60 / 24)}:${pad0((duration / 60 / 60) % 24)}:${pad0(
      (duration / 60) % 60,
    )}:${pad0(duration % 60)}`;
  }
}

function dynamicSpacing(fixedWidth: number, textLen: number, textContent: string) {
  const missingSpaces = fixedWidth - (textLen - DYNAMIC_SPACING.length);
  const spacing = new Array(missingSpaces).fill(' ').join('');
  return [textContent.split(DYNAMIC_SPACING).join(spacing)];
}

function wordsWarping(textContent: string, fixedWidth: number) {
  const replacementLines = [];
  const tokens = textContent.split(' ');

  let lineTokens = [];
  let lineLen = 0;

  for (const token of tokens) {
    const tokenLen = getLen(token);

    if (lineLen + tokenLen >= fixedWidth) {
      replacementLines.push(lineTokens.join(' '));
      lineTokens = [];
      lineLen = 0;
    }

    lineTokens.push(token);
    lineLen += tokenLen + 1;
  }

  if (lineTokens.length > 0) {
    replacementLines.push(lineTokens.join(' '));
  }

  return replacementLines;
}

function getLen(str: string) {
  return str.replace(HTML_ENTITY_RE, ' ').length;
}
